import { NavLink } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import styles from "./NavLinks.module.scss";
import { cloneElement, useId, useRef } from "react";
import { autoUpdate, arrow, offset, useFloating } from "@floating-ui/react";
import { createPortal } from "react-dom";
import useSidebarLinks, { LinkData } from "./useSidebarLinks.tsx";

function LinkItem({
  path,
  icon,
  label,
  accessibleLabel,
  popoverContent,
  annotation,
  isSidebarExpandable,
  iconAnnotation,
}: LinkData & {
  isSidebarExpandable: boolean;
}) {
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    strategy: "fixed",
    whileElementsMounted: (r, f, u) =>
      autoUpdate(r, f, u, { animationFrame: true }),
    placement: isSidebarExpandable ? "bottom-start" : "right-start",
    middleware: [
      offset({
        mainAxis: isSidebarExpandable ? -8 : 20,
        crossAxis: isSidebarExpandable ? 16 : -16,
      }),
      arrow({ element: arrowRef }),
    ],
  });

  return (
    <li>
      <NavLink
        to={path}
        className={styles.link}
        ref={refs.setReference}
        aria-label={accessibleLabel}
      >
        {iconAnnotation && (
          <span className={styles.iconAnnotation}>{iconAnnotation}</span>
        )}
        {icon && <span className={styles.linkIcon}>{icon}</span>}
        <span className={styles.linkText}>{label}</span>
        {annotation && (
          <span className={styles.linkAnnotation}>{annotation}</span>
        )}
      </NavLink>
      {popoverContent &&
        createPortal(
          <div data-sidebar-portal>
            {cloneElement(popoverContent, {
              setFloating: refs.setFloating,
              arrowRef,
              context,
              floatingStyles,
              arrowStaticOffset: isSidebarExpandable ? 8 : undefined,
            })}
          </div>,
          document.body
        )}
    </li>
  );
}

export default function NavLinks({
  isSidebarExpandable,
}: {
  isSidebarExpandable: boolean;
}) {
  const shelfHeadId = useId();
  const { permaLinks, shelfLinks } = useSidebarLinks();

  return (
    <nav>
      <ul>
        {permaLinks.map((link) => (
          <LinkItem
            key={link.path}
            {...link}
            isSidebarExpandable={isSidebarExpandable}
          />
        ))}
      </ul>
      <div className={styles.shelvesHeader} id={shelfHeadId}>
        <FormattedMessage defaultMessage="My Shelves" />
      </div>
      <ul aria-labelledby={shelfHeadId}>
        {shelfLinks.map((link) => (
          <LinkItem
            key={link.path}
            {...link}
            isSidebarExpandable={isSidebarExpandable}
          />
        ))}
      </ul>
    </nav>
  );
}
